import { useEffect, RefObject } from 'react';

/**
 * Custom hook to detect when an element becomes sticky at the top of the viewport
 * Uses a sentinel element and IntersectionObserver for reliable sticky detection
 * Sets a data-sticky attribute on the element when it's stuck at top
 *
 * @param elementRef - Ref to the sticky element to track
 */
const useStickyDetection = (elementRef: RefObject<HTMLElement>) => {
  useEffect(() => {
    const element = elementRef.current;

    // Early exit if element doesn't exist
    if (!element) {
      return undefined;
    }

    // Create a sentinel element just before the sticky element
    // The sentinel must be in the normal document flow with 1px height
    const sentinel = document.createElement('div');
    sentinel.style.position = 'relative';
    sentinel.style.height = '1px';
    sentinel.style.width = '100%';
    sentinel.style.margin = '0 0 -1px 0'; // Negative bottom margin to prevent layout shift
    sentinel.style.padding = '0';
    sentinel.style.opacity = '0';
    sentinel.style.pointerEvents = 'none';
    sentinel.dataset.stickySentinel = '';

    // Insert sentinel before the sticky element
    element.parentElement?.insertBefore(sentinel, element);

    // Observer for the sentinel element
    // Use -1px top margin to account for the 1px sentinel height
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Check if element is visible (not hidden by CSS media query)
        const isVisible = window.getComputedStyle(element).display !== 'none';

        if (isVisible) {
          // When sentinel is NOT intersecting (scrolled past), element is sticky
          if (entry.isIntersecting) {
            delete element.dataset.sticky;
          } else {
            element.dataset.sticky = '';
          }
        } else {
          // Clean up data-sticky when element is hidden
          delete element.dataset.sticky;
        }
      },
      {
        threshold: [0],
        rootMargin: '-1px 0px 0px 0px',
      },
    );

    observer.observe(sentinel);

    // Also check on resize in case viewport changes
    const handleResize = () => {
      const isVisible = window.getComputedStyle(element).display !== 'none';
      if (isVisible) {
        // When element becomes visible again, check if it should be sticky after a brief delay
        // This handles the case where user resizes from desktop to mobile while scrolled
        // The delay ensures CSS transitions and layout changes have completed
        setTimeout(() => {
          const sentinelRect = sentinel.getBoundingClientRect();
          const isScrolledPast = sentinelRect.top <= 0;

          if (isScrolledPast) {
            element.dataset.sticky = '';
          } else {
            delete element.dataset.sticky;
          }
        }, 50); // Small delay to ensure layout has updated
      } else {
        delete element.dataset.sticky;
      }
    };

    window.addEventListener('resize', handleResize, { passive: true });

    // Cleanup function
    return () => {
      observer.disconnect();
      sentinel.remove();
      window.removeEventListener('resize', handleResize);
    };
  }, [elementRef]);
};

export default useStickyDetection;
